/**
 * @license
 *
 * Copyright 2024 Google LLC
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */

/**
 * Model types and helpers — request/response schemas, message and part types,
 * model references, and middleware interfaces for building model plugins.
 *
 * ```ts
 * import { modelRef, type ModelAction, type MessageData } from 'genkit/model';
 * ```
 *
 * @module model
 */

export {
  CandidateErrorSchema,
  CandidateSchema,
  CustomPartSchema,
  DataPartSchema,
  GenerateRequestSchema,
  GenerateResponseChunkSchema,
  GenerateResponseSchema,
  GenerationCommonConfigDescriptions,
  GenerationCommonConfigSchema,
  GenerationUsageSchema,
  MediaPartSchema,
  MessageSchema,
  ModelInfoSchema,
  ModelRequestSchema,
  ModelResponseSchema,
  PartSchema,
  RoleSchema,
  TextPartSchema,
  ToolDefinitionSchema,
  ToolRequestPartSchema,
  ToolResponsePartSchema,
  getBasicUsageStats,
  modelRef,
  simulateConstrainedGeneration,
  type BackgroundModelAction,
  type CandidateData,
  type CandidateError,
  type CustomPart,
  type DataPart,
  type DefineBackgroundModelOptions,
  type DefineModelOptions,
  type GenerateRequest,
  type GenerateRequestData,
  type GenerateResponseChunkData,
  type GenerateResponseData,
  type GenerationCommonConfig,
  type GenerationUsage,
  type MediaPart,
  type MessageData,
  type ModelAction,
  type ModelArgument,
  type ModelInfo,
  type ModelMiddleware,
  type ModelMiddlewareArgument,
  type ModelMiddlewareWithOptions,
  type ModelReference,
  type ModelRequest,
  type ModelResponseChunkData,
  type ModelResponseData,
  type OutputConfig,
  type Part,
  type Role,
  type TextPart,
  type ToolDefinition,
  type ToolRequestPart,
  type ToolResponsePart,
} from '@genkit-ai/ai/model';
